import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Modale } from '../ui/Modale';
import { BoutonPrincipal, BoutonSecondaire } from '../ui/Primitives';
import {
  heures,
  jourCourt,
  messageRefus,
  type FicheFormateur,
  type FactureFormateur,
  type PieceFormateur,
  type SaisieFacture,
} from '../../lib/useFormateurs';

export const CLASSE_CHAMP_FORMATEUR =
  'w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-codialis-navy focus:border-codialis-mint focus:bg-white focus:outline-none';

export function ChampFormateur({
  libelle,
  aide,
  children,
}: {
  libelle: string;
  aide?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <label className="block min-w-0">
      <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">{libelle}</span>
      {children}
      {aide && <span className="mt-1 block text-[11px] text-codialis-gray">{aide}</span>}
    </label>
  );
}

function Refus({ message }: { message: string | null }): JSX.Element | null {
  if (!message) return null;
  return (
    <p role="alert" className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-800">
      {message}
    </p>
  );
}

function PiedModale({
  enCours,
  libelle,
  onAnnuler,
  desactive = false,
}: {
  enCours: boolean;
  libelle: string;
  onAnnuler: () => void;
  desactive?: boolean;
}): JSX.Element {
  return (
    <div className="flex flex-col-reverse gap-2 border-t border-gray-100 pt-4 sm:flex-row sm:justify-end">
      <BoutonSecondaire onClick={onAnnuler}>Annuler</BoutonSecondaire>
      <BoutonPrincipal type="submit" disabled={enCours || desactive}>
        {enCours ? 'Envoi…' : libelle}
      </BoutonPrincipal>
    </div>
  );
}

/** Jour civil d'aujourd'hui à La Réunion, au format des champs date. */
function aujourdHuiIso(): string {
  return new Date().toLocaleDateString('sv-SE', { timeZone: 'Indian/Reunion' });
}

function premierDuMois(iso: string): string {
  return `${iso.slice(0, 8)}01`;
}

/** Ajoute des mois à un jour `AAAA-MM-JJ`, ramené au dernier jour du mois si besoin. */
function plusMois(iso: string, mois: number): string {
  const [annee, moisCourant, jour] = iso.split('-').map(Number) as [number, number, number];
  const cible = new Date(Date.UTC(annee, moisCourant - 1 + mois, 1));
  const dernier = new Date(Date.UTC(cible.getUTCFullYear(), cible.getUTCMonth() + 1, 0)).getUTCDate();
  cible.setUTCDate(Math.min(jour, dernier));
  return cible.toISOString().slice(0, 10);
}

// ---------------------------------------------------------------------------
// Dépôt d'une pièce
// ---------------------------------------------------------------------------

export function ModaleDepotPiece({
  piece,
  enCours,
  onDeposer,
  onFermer,
}: {
  piece: PieceFormateur;
  enCours: boolean;
  onDeposer: (entree: { fichier: File; delivreLe?: string; valableJusquAu?: string }) => Promise<void>;
  onFermer: () => void;
}): JSX.Element {
  const [fichier, setFichier] = useState<File | null>(null);
  const [date, setDate] = useState('');
  const [refus, setRefus] = useState<string | null>(null);

  const soumettre = async (evenement: FormEvent): Promise<void> => {
    evenement.preventDefault();
    if (!fichier) {
      setRefus('Choisissez le fichier à déposer.');
      return;
    }
    setRefus(null);
    try {
      await onDeposer({
        fichier,
        ...(piece.date === 'DELIVRANCE' && date ? { delivreLe: date } : {}),
        ...(piece.date === 'ECHEANCE' && date ? { valableJusquAu: date } : {}),
      });
    } catch (erreur) {
      setRefus(messageRefus(erreur, 'Le dépôt a échoué.'));
    }
  };

  return (
    <Modale ouverte titre={`Déposer · ${piece.libelle}`} sousTitre={piece.detail} onFermer={onFermer} largeur="max-w-lg">
      <form onSubmit={soumettre} className="space-y-4 p-4 sm:p-6">
        <ChampFormateur libelle="Fichier" aide="PDF, JPEG ou PNG · 10 Mo au plus">
          <input
            type="file"
            accept="application/pdf,image/jpeg,image/png"
            onChange={(e) => setFichier(e.target.files?.[0] ?? null)}
            className={CLASSE_CHAMP_FORMATEUR}
          />
        </ChampFormateur>
        {piece.date === 'DELIVRANCE' && (
          <ChampFormateur libelle="Date de délivrance" aide="L’attestation vaut six mois à partir de cette date.">
            <input
              type="date"
              value={date}
              max={aujourdHuiIso()}
              onChange={(e) => setDate(e.target.value)}
              className={CLASSE_CHAMP_FORMATEUR}
            />
          </ChampFormateur>
        )}
        {piece.date === 'ECHEANCE' && (
          <ChampFormateur libelle="Valable jusqu’au" aide="Fin de la période couverte, lue sur l’attestation.">
            <input
              type="date"
              value={date}
              min={aujourdHuiIso()}
              onChange={(e) => setDate(e.target.value)}
              className={CLASSE_CHAMP_FORMATEUR}
            />
          </ChampFormateur>
        )}
        <Refus message={refus} />
        <PiedModale enCours={enCours} libelle="Déposer" onAnnuler={onFermer} />
      </form>
    </Modale>
  );
}

// ---------------------------------------------------------------------------
// Facture
// ---------------------------------------------------------------------------

export function ModaleFacture({
  fiche,
  facture,
  enCours,
  onEnvoyer,
  onFermer,
}: {
  fiche: FicheFormateur;
  /** Facture à corriger ; absente, c'est une transmission. */
  facture: FactureFormateur | null;
  enCours: boolean;
  onEnvoyer: (saisie: SaisieFacture) => Promise<void>;
  onFermer: () => void;
}): JSX.Element {
  const aujourdHui = aujourdHuiIso();
  const [numero, setNumero] = useState(facture?.numero ?? '');
  const [debut, setDebut] = useState(facture?.debut ?? premierDuMois(aujourdHui));
  const [fin, setFin] = useState(facture?.fin ?? aujourdHui);
  const [montant, setMontant] = useState(facture ? String(facture.montant).replace('.', ',') : '');
  const [site, setSite] = useState(facture?.siteCode ?? (fiche.sites.length === 1 ? fiche.sites[0]!.code : ''));
  const [fichier, setFichier] = useState<File | null>(null);
  const [refus, setRefus] = useState<string | null>(null);

  // Repère affiché : les fiches validées non facturées de la période et de
  // l'antenne — l'API refait le même tri à la transmission.
  const fiches = useMemo(() => {
    const disponibles = [
      ...fiche.fichesFacturables,
      ...(facture?.fiches.map((ligne) => ({ ...ligne, siteCode: facture.siteCode })) ?? []),
    ];
    return disponibles.filter(
      (ligne) =>
        ligne.date !== null && ligne.date >= debut && ligne.date <= fin && (!site || ligne.siteCode === site),
    );
  }, [fiche.fichesFacturables, facture, debut, fin, site]);
  const totalHeures = fiches.reduce((total, ligne) => total + ligne.heures, 0);

  const soumettre = async (evenement: FormEvent): Promise<void> => {
    evenement.preventDefault();
    if (!facture && !fichier) {
      setRefus('Joignez le PDF de votre facture.');
      return;
    }
    setRefus(null);
    try {
      await onEnvoyer({ numero, debut, fin, montant, site, fichier });
    } catch (erreur) {
      setRefus(messageRefus(erreur, 'La transmission a échoué.'));
    }
  };

  return (
    <Modale
      ouverte
      titre={facture ? `Corriger la facture n° ${facture.numero}` : 'Transmettre une facture'}
      sousTitre="Elle part à l’assistant(e) pédagogique ou administratif pour vérification."
      onFermer={onFermer}
      largeur="max-w-2xl"
    >
      <form onSubmit={soumettre} className="space-y-4 p-4 sm:p-6">
        {facture?.motifRenvoi && (
          <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-800">Motif du renvoi : {facture.motifRenvoi}</p>
        )}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <ChampFormateur libelle="N° de facture">
            <input
              value={numero}
              onChange={(e) => setNumero(e.target.value)}
              maxLength={60}
              required
              className={CLASSE_CHAMP_FORMATEUR}
            />
          </ChampFormateur>
          <ChampFormateur libelle="Montant de la facture (€)" aide="Total à régler, tel qu’il figure sur votre facture.">
            <input
              value={montant}
              onChange={(e) => setMontant(e.target.value)}
              inputMode="decimal"
              required
              className={`${CLASSE_CHAMP_FORMATEUR} font-mono`}
            />
          </ChampFormateur>
          <ChampFormateur libelle="Du">
            <input
              type="date"
              value={debut}
              max={aujourdHui}
              onChange={(e) => setDebut(e.target.value)}
              required
              className={CLASSE_CHAMP_FORMATEUR}
            />
          </ChampFormateur>
          <ChampFormateur libelle="Au">
            <input
              type="date"
              value={fin}
              onChange={(e) => setFin(e.target.value)}
              required
              className={CLASSE_CHAMP_FORMATEUR}
            />
          </ChampFormateur>
          {fiche.sites.length > 1 && (
            <ChampFormateur libelle="Antenne">
              <select value={site} onChange={(e) => setSite(e.target.value)} required className={CLASSE_CHAMP_FORMATEUR}>
                <option value="">Choisir…</option>
                {fiche.sites.map((option) => (
                  <option key={option.code} value={option.code}>
                    {option.nom}
                  </option>
                ))}
              </select>
            </ChampFormateur>
          )}
          <ChampFormateur
            libelle="PDF de la facture"
            aide={facture ? `Facultatif : sans nouveau fichier, « ${facture.nomFichier} » est conservé.` : 'PDF · 10 Mo au plus'}
          >
            <input
              type="file"
              accept="application/pdf"
              onChange={(e) => setFichier(e.target.files?.[0] ?? null)}
              className={CLASSE_CHAMP_FORMATEUR}
            />
          </ChampFormateur>
        </div>

        <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
          <div className="text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
            Fiches séquence validées de la période
          </div>
          {fiches.length === 0 ? (
            <p className="mt-1 text-xs text-codialis-gray">
              Aucune fiche validée sur cette période : l’assistant(e) vérifiera le montant sans ce repère.
            </p>
          ) : (
            <ul className="mt-1 space-y-0.5 text-xs text-codialis-navy">
              {fiches.map((ligne) => (
                <li key={ligne.id}>
                  {jourCourt(ligne.date)} · {ligne.titre} · {heures(ligne.heures)}
                </li>
              ))}
              <li className="pt-1 font-semibold">
                Total : {heures(totalHeures)}
                {fiche.tauxHoraire !== null &&
                  ` × ${fiche.tauxHoraire.toLocaleString('fr-FR')} € HT = ${(totalHeures * fiche.tauxHoraire).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })} HT`}
              </li>
            </ul>
          )}
        </div>

        <Refus message={refus} />
        <PiedModale enCours={enCours} libelle={facture ? 'Transmettre la correction' : 'Transmettre'} onAnnuler={onFermer} />
      </form>
    </Modale>
  );
}

// ---------------------------------------------------------------------------
// Convention
// ---------------------------------------------------------------------------

export interface SaisieConvention {
  debut: string;
  fin: string;
  formationCodes: string[];
  heuresPrevues: string;
}

export function ModaleConvention({
  formations,
  enCours,
  onGenerer,
  onFermer,
}: {
  /** Fournies au pôle seulement : le formateur laisse la plateforme les déduire. */
  formations?: { code: string; titre: string }[];
  enCours: boolean;
  onGenerer: (saisie: SaisieConvention) => Promise<void>;
  onFermer: () => void;
}): JSX.Element {
  const aujourdHui = aujourdHuiIso();
  const [debut, setDebut] = useState(aujourdHui);
  const [fin, setFin] = useState(plusMois(aujourdHui, 6));
  const [choisies, setChoisies] = useState<string[]>([]);
  const [volume, setVolume] = useState('');
  const [refus, setRefus] = useState<string | null>(null);

  useEffect(() => {
    if (fin < debut) setFin(debut);
  }, [debut, fin]);

  const soumettre = async (evenement: FormEvent): Promise<void> => {
    evenement.preventDefault();
    setRefus(null);
    try {
      await onGenerer({ debut, fin, formationCodes: choisies, heuresPrevues: volume });
    } catch (erreur) {
      setRefus(messageRefus(erreur, 'La génération a échoué.'));
    }
  };

  return (
    <Modale
      ouverte
      titre="Générer la convention de formation"
      sousTitre="Composée depuis la fiche du formateur, son taux et les séances qui lui sont attribuées."
      onFermer={onFermer}
      largeur="max-w-lg"
    >
      <form onSubmit={soumettre} className="space-y-4 p-4 sm:p-6">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <ChampFormateur libelle="Du">
            <input type="date" value={debut} onChange={(e) => setDebut(e.target.value)} required className={CLASSE_CHAMP_FORMATEUR} />
          </ChampFormateur>
          <ChampFormateur libelle="Au">
            <input type="date" value={fin} min={debut} onChange={(e) => setFin(e.target.value)} required className={CLASSE_CHAMP_FORMATEUR} />
          </ChampFormateur>
        </div>
        {formations ? (
          <>
            <fieldset>
              <legend className="mb-1 text-[11px] font-bold uppercase tracking-wide text-codialis-navy">Formations</legend>
              <p className="mb-2 text-[11px] text-codialis-gray">
                Sans choix, celles des séances attribuées et des dossiers suivis.
              </p>
              <div className="grid max-h-48 grid-cols-1 gap-1 overflow-y-auto rounded-xl border border-gray-100 p-2 sm:grid-cols-2">
                {formations.map((formation) => (
                  <label key={formation.code} className="flex min-h-[40px] items-center gap-2 text-sm text-codialis-navy">
                    <input
                      type="checkbox"
                      checked={choisies.includes(formation.code)}
                      onChange={(e) =>
                        setChoisies((liste) =>
                          e.target.checked ? [...liste, formation.code] : liste.filter((code) => code !== formation.code),
                        )
                      }
                      className="h-5 w-5 shrink-0"
                    />
                    <span className="min-w-0">{formation.titre}</span>
                  </label>
                ))}
              </div>
            </fieldset>
            <ChampFormateur libelle="Volume prévisionnel (heures)" aide="Vide : la somme des séances attribuées sur la période.">
              <input value={volume} onChange={(e) => setVolume(e.target.value)} inputMode="decimal" className={CLASSE_CHAMP_FORMATEUR} />
            </ChampFormateur>
          </>
        ) : (
          <p className="text-xs text-codialis-gray">
            Formations, volume et séances viennent du planning tenu par le pôle pédagogique ; ce qui manque reste à
            compléter à la main avant signature.
          </p>
        )}
        <Refus message={refus} />
        <PiedModale enCours={enCours} libelle="Générer" onAnnuler={onFermer} />
      </form>
    </Modale>
  );
}

// ---------------------------------------------------------------------------
// Motif d'un refus ou d'un renvoi
// ---------------------------------------------------------------------------

export function ModaleMotif({
  titre,
  sousTitre,
  libelle,
  enCours,
  onValider,
  onFermer,
}: {
  titre: string;
  sousTitre: string;
  libelle: string;
  enCours: boolean;
  onValider: (motif: string) => Promise<void>;
  onFermer: () => void;
}): JSX.Element {
  const [motif, setMotif] = useState('');
  const [refus, setRefus] = useState<string | null>(null);

  const soumettre = async (evenement: FormEvent): Promise<void> => {
    evenement.preventDefault();
    if (!motif.trim()) {
      setRefus('Indiquez le motif : le formateur doit savoir quoi corriger.');
      return;
    }
    setRefus(null);
    try {
      await onValider(motif.trim());
    } catch (erreur) {
      setRefus(messageRefus(erreur, 'L’enregistrement a échoué.'));
    }
  };

  return (
    <Modale ouverte titre={titre} sousTitre={sousTitre} onFermer={onFermer} largeur="max-w-lg">
      <form onSubmit={soumettre} className="space-y-4 p-4 sm:p-6">
        <ChampFormateur libelle="Motif">
          <textarea
            value={motif}
            onChange={(e) => setMotif(e.target.value)}
            rows={4}
            maxLength={500}
            className={CLASSE_CHAMP_FORMATEUR}
          />
        </ChampFormateur>
        <Refus message={refus} />
        <PiedModale enCours={enCours} libelle={libelle} onAnnuler={onFermer} />
      </form>
    </Modale>
  );
}
